import { updateComplianceItem, useCompanyData } from '@commenda/api';
import {
  Alert,
  Avatar,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FieldWrapper,
  Icons,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  MultiSelect,
  useToggle,
} from '@commenda/kakashi';
import { TComplianceItem } from '@commenda/types/src/compliance-calendar/compliances/types';
import { useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { filterCommendaIoEmails } from '@commenda/utils';

export const AddAssigneeModal = ({
  isOpen,
  onClose,
  filing,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  filing: TComplianceItem;
  onSuccess: () => void;
}) => {
  const { setNotification } = useNotification();

  const isAddingAssignees = useToggle();
  const [requiredError, setRequiredError] = useState<string | null>(null);

  const { data: company } = useCompanyData(filing.company.id);

  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);

  const existingAssigneeIds =
    filing.assignees?.map((assignee) => assignee.id) ?? [];

  // filter @commenda.io emails only on production
  const filteredMembers = filterCommendaIoEmails(company?.members, filing.company.id);

  const members: { value: string; label: string }[] = (
    filteredMembers.map((member) => ({
      value: member.id,
      label: member.name,
    })) ?? []
  ).filter((member) => !existingAssigneeIds.includes(member.value));

  const hasAvailableMembers = members.length > 0;

  const handleClose = () => {
    setRequiredError(null);
    setSelectedAssignees([]);
    onClose();
  };

  const onSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setRequiredError(null);

    if (selectedAssignees.length === 0) {
      setRequiredError('At least one member must be selected.');
      return;
    }

    isAddingAssignees.on();
    const alreadyAssignedAssignees =
      filing.assignees?.map((assignee) => assignee.id) ?? [];

    const addedAssignees = selectedAssignees.filter(
      (assigneeId) => !alreadyAssignedAssignees.includes(assigneeId)
    );

    const res = await updateComplianceItem(filing.id, {
      assigneeIdsToAdd: addedAssignees,
    });

    if (!res.success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to add assignees',
      });
      isAddingAssignees.off();
      return;
    }

    isAddingAssignees.off();
    onSuccess();
    handleClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col gap-xs items-start">
          <DialogTitle>Add assignees</DialogTitle>
          {hasAvailableMembers && (
            <DialogDescription>
              You are adding assignees to&nbsp;
              <span className="font-bold">{filing.title}</span> for{' '}
              {filing.corporation.companyDisplayName}.
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="p-3xl flex flex-col gap-4xl">
          {!hasAvailableMembers && (
            <Alert
              variant="info"
              description="You have no team members to add to this filing."
              icon={
                <Icons.InfoCircle className="size-5 text-blue-500 shrink-0" />
              }
            />
          )}
          <FieldWrapper>
            <Label htmlFor="assignees">Select team members</Label>
            <MultiSelect
              error={requiredError}
              id="assignees"
              options={members}
              placeholder="Select assignees"
              onValueChange={(value) => {
                setSelectedAssignees(value);
                setRequiredError(null);
              }}
              disabled={!hasAvailableMembers}
              renderOption={({ option, isSelected, onSelect }) => (
                <div
                  className="flex items-center justify-between w-full"
                  onClick={() => onSelect(option.value)}
                >
                  <div className="flex items-center gap-2">
                    <Avatar size="xs" name={option.label} />
                    <span>{option.label}</span>
                  </div>
                  {isSelected ? (
                    <Icons.Check className="ml-auto size-4" />
                  ) : null}
                </div>
              )}
              defaultValue={selectedAssignees}
            />
            {requiredError ? (
              <InputErrorMessage message={requiredError} />
            ) : null}
          </FieldWrapper>
        </div>
        <DialogFooter className="border-t-0">
          <Button size="sm" variant="secondary" onClick={handleClose}>
            Back
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={onSubmit}
            disabled={isAddingAssignees.value || !hasAvailableMembers}
          >
            {isAddingAssignees.value ? <LoadingIndicator size={16} /> : null}
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
